/**
 * Bundle discount validation utilities for cart items.
 * Fetches current bundle discount amounts from Shopify product metafields to ensure up-to-date pricing.
 */

import { getBundleDefinitionProduct, getProductByID } from '@/lib/shopify';

/**
 * Identifies cart items that need bundle discount refresh from current product metafields
 * If any product in a bundle is no longer valid, all items from that bundle will be marked for removal
 * @param {Object} cart - The cart object with line items
 * @returns {Array} Array of items that need bundle discount updates
 */
export async function identifyItemsNeedingBundleRefresh(cart) {
  if (!cart?.lines?.length) {
    return [];
  }

  // Group items by their bundle settings product ID for batch processing
  const bundleSettingsGroups = new Map();
  const bundleItems = [];

  for (const item of cart.lines) {
    const bundleHandleAttr = item.attributes?.find(
      (attr) => attr.key === '_bundleHandle'
    );
    const bundleDiscountAttr = item.attributes?.find((attr) =>
      attr.key.includes('bundleDiscountAmount')
    );
    const bundleSettingsProductId = item.attributes?.find(
      (attr) => attr.key === '_bundleSettingsProductId'
    )?.value;

    if (bundleHandleAttr && bundleDiscountAttr && bundleSettingsProductId) {
      const itemData = {
        item,
        bundleHandle: bundleHandleAttr.value,
        bundleDiscountAttr,
        bundleSettingsProductId
      };

      bundleItems.push(itemData);

      if (!bundleSettingsGroups.has(bundleSettingsProductId)) {
        bundleSettingsGroups.set(bundleSettingsProductId, []);
      }
      bundleSettingsGroups.get(bundleSettingsProductId).push(itemData);
    }
  }

  if (bundleItems.length === 0) {
    return [];
  }

  // Fetch all unique bundle products and settings in batch
  const bundleDataCache = new Map();
  const bundleProductPromises = Array.from(bundleSettingsGroups.keys()).map(
    async (bundleSettingsProductId) => {
      try {
        const bundleProduct = await getProductByID(bundleSettingsProductId);
        if (!bundleProduct) {
          return { bundleSettingsProductId, error: 'Bundle product not found' };
        }

        const bundleSettings = await getBundleDefinitionProduct(
          bundleProduct.id
        );
        if (!bundleSettings?.bundlePriceData?.value) {
          return {
            bundleSettingsProductId,
            error: 'No bundle price data found'
          };
        }

        const bundlePricingMetafield = JSON.parse(
          bundleSettings.bundlePriceData.value
        );
        return {
          bundleSettingsProductId,
          bundleProduct,
          bundleSettings,
          bundlePricingMetafield
        };
      } catch (error) {
        return { bundleSettingsProductId, error: error.message };
      }
    }
  );

  const bundleDataResults = await Promise.all(bundleProductPromises);

  // Cache successful results
  for (const result of bundleDataResults) {
    bundleDataCache.set(result.bundleSettingsProductId, result);
  }

  const itemsNeedingRefresh = [];
  const bundlesToRemove = new Set();

  // Process each bundle group
  for (const [bundleSettingsProductId, items] of bundleSettingsGroups) {
    const bundleData = bundleDataCache.get(bundleSettingsProductId);

    // If bundle data couldn't be loaded, mark entire bundle for removal
    if (bundleData.error) {
      console.warn(
        `Bundle error for ${bundleSettingsProductId}: ${bundleData.error}`
      );
      const bundleHandle = items[0].bundleHandle;
      bundlesToRemove.add(bundleHandle);

      // Add all items from this bundle to the removal list
      for (const { item, bundleDiscountAttr } of items) {
        itemsNeedingRefresh.push({
          item,
          currentDiscount: '0',
          reason: 'product_removed_from_bundle',
          oldDiscount: bundleDiscountAttr.value,
          newDiscount: '0',
          action: 'remove'
        });
      }
      continue;
    }

    const { bundlePricingMetafield } = bundleData;

    // Check if any product in this bundle group is no longer valid
    let bundleHasInvalidProduct = false;

    for (const { item, bundleHandle } of items) {
      const cartItemProductId = parseInt(
        item.merchandise.product.id.replace('gid://shopify/Product/', '')
      );

      let matchingProduct = bundlePricingMetafield.products?.find(
        (product) => product.id === cartItemProductId
      );

      // Handle swappable products - if we can't find by current product ID,
      // try using the original bundle product ID from cart attributes
      if (!matchingProduct) {
        const originalBundleProductId = item.attributes?.find(
          (attr) => attr.key === '_originalBundleProductId'
        )?.value;

        if (originalBundleProductId) {
          matchingProduct = bundlePricingMetafield.products?.find(
            (product) => product.id.toString() === originalBundleProductId
          );
        }
      }

      if (!matchingProduct) {
        console.warn(
          `Product ID ${cartItemProductId} not found in bundle pricing data - product may have been removed from bundle`
        );
        bundlesToRemove.add(bundleHandle);
        bundleHasInvalidProduct = true;
        break;
      }
    }

    if (bundleHasInvalidProduct) {
      // Add all items from this invalid bundle to the removal list
      for (const { item, bundleDiscountAttr } of items) {
        itemsNeedingRefresh.push({
          item,
          currentDiscount: '0',
          reason: 'product_removed_from_bundle',
          oldDiscount: bundleDiscountAttr.value,
          newDiscount: '0',
          action: 'remove'
        });
      }
      continue; // Skip to next bundle group
    }

    // Process valid items in this bundle group
    for (const { item, bundleHandle, bundleDiscountAttr } of items) {
      try {
        const cartItemProductId = parseInt(
          item.merchandise.product.id.replace('gid://shopify/Product/', '')
        );
        const cartItemVariantTitle = item.merchandise.title;

        let matchingProduct = bundlePricingMetafield.products?.find(
          (product) => product.id === cartItemProductId
        );

        // Handle swappable products if we can't find by product ID,
        // use the original bundle product ID stored in cart attributes
        if (!matchingProduct) {
          const originalBundleProductId = item.attributes?.find(
            (attr) => attr.key === '_originalBundleProductId'
          )?.value;

          if (originalBundleProductId) {
            matchingProduct = bundlePricingMetafield.products?.find(
              (product) => product.id.toString() === originalBundleProductId
            );
          }

          // If still no match, try fallback logic for older cart items without _originalBundleProductId
          if (!matchingProduct) {
            const cartItemProductType = item.attributes?.find(
              (attr) => attr.key === '_productType'
            )?.value;

            if (cartItemProductType === 'Mattress') {
              // For mattresses, find any mattress product in the bundle
              // This is a fallback for existing cart items that don't have _originalBundleProductId
              matchingProduct = bundlePricingMetafield.products?.[0];
            }
          }
        }

        if (!matchingProduct) {
          console.warn(
            `Product with ID ${cartItemProductId} not found in bundle pricing metafield - removing from bundle`
          );

          bundlesToRemove.add(bundleHandle);
          itemsNeedingRefresh.push({
            item,
            currentDiscount: '0',
            reason: 'product_removed_from_bundle',
            oldDiscount: bundleDiscountAttr.value,
            newDiscount: '0',
            action: 'remove'
          });
          continue;
        }

        // Find matching variant
        let variant = matchingProduct.variants?.find(
          (variant) => variant.title === cartItemVariantTitle
        );

        if (!variant) {
          variant = matchingProduct.variants?.find((variant) =>
            cartItemVariantTitle.startsWith(variant.title)
          );
        }

        if (!variant) {
          console.warn(
            `Variant "${cartItemVariantTitle}" not found for product ${matchingProduct.title} - variant may have been removed from bundle`
          );
          bundlesToRemove.add(bundleHandle);
          itemsNeedingRefresh.push({
            item,
            currentDiscount: '0',
            reason: 'product_removed_from_bundle',
            oldDiscount: bundleDiscountAttr.value,
            newDiscount: '0',
            action: 'remove'
          });
          continue;
        }
        const currentDiscountAmount = variant.discount
          ? variant.discount.toString()
          : null;

        // Check if discount metafield has been updated
        if (currentDiscountAmount !== null) {
          const storedDiscount = bundleDiscountAttr.value;
          const currentDiscount = currentDiscountAmount;
          const itemPrice = parseFloat(item.merchandise.price.amount) || 0;

          // Sanitize both values - never allow negative discounts or values exceeding item price
          const safeCurrentDiscount = Math.max(
            0,
            Math.min(parseFloat(currentDiscount) || 0, itemPrice)
          );
          const safeStoredDiscount = Math.max(
            0,
            Math.min(parseFloat(storedDiscount) || 0, itemPrice)
          );

          // Only update if the sanitized values are actually different
          if (safeStoredDiscount !== safeCurrentDiscount) {
            itemsNeedingRefresh.push({
              item,
              currentDiscount: safeCurrentDiscount.toString(),
              reason: 'metafield_updated',
              oldDiscount: storedDiscount,
              newDiscount: safeCurrentDiscount.toString()
            });
          }
        }

        // Validate that stored discount doesn't exceed item price and isn't negative
        const storedDiscount = parseFloat(bundleDiscountAttr.value) || 0;
        const itemPrice = parseFloat(item.merchandise.price.amount) || 0;

        // Only flag if not already processed above and needs correction
        const alreadyProcessed = itemsNeedingRefresh.some(
          (processedItem) => processedItem.item.id === item.id
        );
        if (
          !alreadyProcessed &&
          (storedDiscount < 0 || (itemPrice > 0 && storedDiscount > itemPrice))
        ) {
          // If discount is negative, set to 0. If it exceeds price, set to price (but never negative)
          const correctedDiscount =
            storedDiscount < 0 ? 0 : Math.max(0, itemPrice);
          itemsNeedingRefresh.push({
            item,
            currentDiscount: correctedDiscount.toString(),
            reason:
              storedDiscount < 0
                ? 'negative_discount'
                : 'discount_exceeds_price',
            oldDiscount: storedDiscount.toString(),
            newDiscount: correctedDiscount.toString()
          });
        }
      } catch (error) {
        console.error(
          `Error validating bundle discount for item ${item.id}:`,
          error
        );
      }
    }
  }

  return itemsNeedingRefresh;
}

/**
 * Creates fixed line items with corrected bundle discounts
 * @param {Array} itemsToFix - Items identified as needing bundle discount fixes
 * @returns {Array} Updated line items for cart update mutation
 */
export function createBundleDiscountFixes(itemsToFix) {
  const result = itemsToFix.map(({ item, currentDiscount, reason }) => {
    let updatedAttributes;

    if (reason === 'product_removed_from_bundle') {
      // Remove bundle-related attributes when product is no longer in bundle
      updatedAttributes = item.attributes.filter(
        (attr) =>
          !attr.key.includes('bundleDiscountAmount') &&
          attr.key !== '_bundleHandle'
      );

      // Remove the item from cart by setting quantity to 0
      return {
        id: item.id,
        quantity: 0,
        attributes: updatedAttributes
      };
    } else {
      // Update discount amount for other scenarios
      updatedAttributes = item.attributes.map((attr) => {
        if (attr.key.includes('bundleDiscountAmount')) {
          return {
            ...attr,
            value: currentDiscount.toString()
          };
        }
        return attr;
      });

      const fixedItem = {
        id: item.id,
        quantity: item.quantity,
        attributes: updatedAttributes
      };

      return fixedItem;
    }
  });
  return result;
}
/**
 * Main function to validate and fix bundle discounts that exceed item prices
 * @param {Object} cart - The cart object
 * @returns {Object} { needsRefresh: boolean, fixedItems: Array, itemCount: number, removedFromBundle: Array }
 */
export async function validateAndRefreshBundleDiscounts(cart) {
  try {
    const itemsNeedingRefresh = await identifyItemsNeedingBundleRefresh(cart);
    if (itemsNeedingRefresh.length === 0) {
      return { needsRefresh: false, fixedItems: [], removedFromBundle: [] };
    }

    const fixedItems = createBundleDiscountFixes(itemsNeedingRefresh);
    // Separate items removed from bundle for potential additional handling
    const removedFromBundle = itemsNeedingRefresh.filter(
      (item) => item.reason === 'product_removed_from_bundle'
    );

    return {
      needsRefresh: true,
      fixedItems,
      itemCount: itemsNeedingRefresh.length,
      removedFromBundle
    };
  } catch (error) {
    console.error('Error validating bundle discounts:', error);
    return { needsRefresh: false, fixedItems: [], removedFromBundle: [] };
  }
}
